// The margin (the Mac's margin comments beside the text, and Revise ▸ Comments' rows): each comment with its author,
// date, quote, text and replies — Reply, Resolve and Delete — and each suggestion struck through with its replacement —
// Accept (the text changes first, then the status) and Reject. Agents' comments are marked as theirs. Choosing one
// selects its passage in the editor (or opens it there, from Revise).
import { useEffect, useRef, useState } from 'react';
import type { DocumentEditor, ProjectEditor } from '@writing-suite/core';
import type { LiveProject } from '../store/project.ts';
import { announce } from '../shell/context.ts';
import { isDetached, resolveEntry, type MarginEntry } from './comments.ts';
import { CommentAuthor } from './People.tsx';
import { categoryOf, type CommentCategory } from '@writing-suite/core/revise';
import { CategorySelect, CategoryTag } from './CommentCategories.tsx';

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const when = (d: Date) => (Number.isFinite(d?.getTime?.()) && d.getTime() > 0 ? DATE.format(d) : '');

/** The typed writes beyond the contract (suggestions, synopsis comments) that `editProject` also makes. */
const more = (e: ProjectEditor) => e as unknown as DocumentEditor;

export interface MarginActions {
  live: LiveProject | null;
  /** Accept and reject suggestions, resolve and delete comments (the owner and co-writers). */
  canWrite: boolean;
  /** Reply (every role, a commenter's included, W9a); defaults to `canWrite`. */
  canComment?: boolean;
  /** Replaces a passage through the open editor (undoable) when the fragment is in it; false otherwise. */
  replaceInEditor?(fragmentId: string, from: number, to: number, insert: string): boolean;
}

/** Accept: the replacement goes in where the passage is now (through the editor if it's open), then the status. */
export function acceptSuggestion(entry: MarginEntry, a: MarginActions): boolean {
  const live = a.live;
  const doc = live?.doc(`fragment.${entry.fragmentId}`);
  const range = doc ? resolveEntry(doc, entry) : null;
  if (!live || !range) {
    announce('The suggested passage has since changed; the suggestion can’t be applied.');
    return false;
  }
  const proposed = entry.proposed ?? '';
  if (!a.replaceInEditor?.(entry.fragmentId, range[0], range[1], proposed)) {
    live.edit((e) => e.spliceText(entry.fragmentId, range[0], range[1] - range[0], proposed));
  }
  live.edit((e) => more(e).setSuggestionStatus(entry.fragmentId, entry.id, 'accepted'));
  announce(`Accepted the suggestion from ${entry.author}`);
  return true;
}

export function rejectSuggestion(entry: MarginEntry, a: MarginActions) {
  a.live?.edit((e) => more(e).setSuggestionStatus(entry.fragmentId, entry.id, 'rejected'));
  announce(`Rejected the suggestion from ${entry.author}`);
}

export function resolveComment(entry: MarginEntry, a: MarginActions) {
  a.live?.edit((e) => (entry.kind === 'synopsis' ? more(e).setSynopsisCommentStatus(entry.fragmentId, entry.id, 'resolved') : e.setCommentStatus(entry.fragmentId, entry.id, 'resolved')));
  announce('Resolved');
}

/** One comment or suggestion. `onChoose`: selects (or shows) its passage. */
export function MarginCard({
  entry,
  active,
  actions,
  onChoose,
  showScene,
  chooseLabel = 'Select the passage',
  categories = [],
  onEditCategories,
}: {
  entry: MarginEntry;
  active: boolean;
  actions: MarginActions;
  onChoose?: () => void;
  showScene?: boolean;
  chooseLabel?: string;
  /** The project's comment categories: the comment's is named on the card, and a writer can choose it. */
  categories?: readonly CommentCategory[];
  /** Opens the sheet that names the categories. */
  onEditCategories?: () => void;
}) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const write = actions.canWrite && !!actions.live;
  const comment = (actions.canComment ?? actions.canWrite) && !!actions.live;
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);
  const detached = isDetached(entry);
  const category = entry.kind === 'suggestion' ? null : categoryOf(entry, categories);
  const kindClass = entry.kind === 'suggestion' ? 'suggestion' : entry.authorKind === 'agent' ? 'agent' : 'human';
  const sendReply = () => {
    const body = reply.trim();
    if (!body || !actions.live) return;
    actions.live.edit((e) => e.reply(entry.fragmentId, entry.id, body));
    setReply('');
    setReplying(false);
    announce('Replied');
  };
  return (
    <article
      ref={ref}
      className={`margin-card ${kindClass}${active ? ' active' : ''}${detached ? ' detached' : ''}`}
      data-entry-id={entry.id}
      data-kind={entry.kind}
      aria-label={`${entry.kind === 'suggestion' ? 'Suggestion' : 'Comment'} from ${entry.author}`}
    >
      <p className="comment-meta">
        <CommentAuthor live={actions.live} fragmentId={entry.fragmentId} kind={entry.kind} id={entry.id} signed={entry.author} />
        {entry.authorKind === 'agent' ? <span className="agent-badge">Agent</span> : null}
        {showScene ? <span className="comment-scene"> · {entry.sceneTitle}</span> : null}
        {when(entry.createdAt) ? <span> · {when(entry.createdAt)}</span> : null}
        {category ? <CategoryTag category={category} /> : null}
      </p>
      {entry.kind === 'suggestion' ? (
        <button type="button" className="quote-button suggestion-change" onClick={onChoose} title={chooseLabel} disabled={!onChoose}>
          <del>{entry.quote || '(insert)'}</del>
          <span aria-hidden="true"> → </span>
          <ins>{entry.proposed || '(cut)'}</ins>
        </button>
      ) : entry.kind === 'synopsis' ? (
        <button type="button" className="quote-button" onClick={onChoose} disabled={!onChoose}>
          {entry.quote ? `On the synopsis: “${entry.quote}”` : 'On the synopsis'}
        </button>
      ) : entry.quote ? (
        <button type="button" className="quote-button comment-quote" onClick={onChoose} title={chooseLabel} disabled={!onChoose}>
          “{entry.quote}”
        </button>
      ) : null}
      {detached ? <p className="detached-note">The passage isn’t in the text any more.</p> : null}
      {entry.body ? <p className="comment-body">{entry.body}</p> : null}
      {entry.replies.length ? (
        <ul className="replies" aria-label="Replies">
          {entry.replies.map((r) => (
            <li key={r.id} className={`reply ${r.authorKind}`} data-comment-id={r.id}>
              <p className="comment-meta">
                <CommentAuthor live={actions.live} fragmentId={entry.fragmentId} kind="comment" id={r.id} signed={r.author} />
                {r.authorKind === 'agent' ? <span className="agent-badge">Agent</span> : null}
                {when(r.createdAt) ? <span> · {when(r.createdAt)}</span> : null}
              </p>
              <p className="comment-body">{r.body}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {comment && !write && entry.kind === 'comment' ? (
        <div className="card-actions">
          <button type="button" className="link-button" onClick={() => setReplying(!replying)} aria-expanded={replying}>
            Reply
          </button>
        </div>
      ) : null}
      {write ? (
        <div className="card-actions">
          {entry.kind === 'suggestion' ? (
            <>
              <button type="button" className="link-button" onClick={() => acceptSuggestion(entry, actions)} disabled={detached}>
                Accept
              </button>
              <button type="button" className="link-button" onClick={() => rejectSuggestion(entry, actions)}>
                Reject
              </button>
            </>
          ) : (
            <>
              {onChoose && entry.kind !== 'synopsis' && chooseLabel !== 'Select the passage' ? (
                <button type="button" className="link-button" onClick={onChoose}>
                  Show
                </button>
              ) : null}
              {entry.kind === 'comment' ? (
                <button type="button" className="link-button" onClick={() => setReplying(!replying)} aria-expanded={replying}>
                  Reply
                </button>
              ) : null}
              <button type="button" className="link-button" onClick={() => resolveComment(entry, actions)}>
                Resolve
              </button>
              {entry.kind === 'comment' ? (
                confirmDelete ? (
                  <>
                    <button
                      type="button"
                      className="link-button danger"
                      onClick={() => {
                        actions.live?.edit((e) => e.removeComment(entry.fragmentId, entry.id));
                        announce('Deleted');
                      }}
                    >
                      Delete it
                    </button>
                    <button type="button" className="link-button" onClick={() => setConfirmDelete(false)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button type="button" className="link-button" onClick={() => setConfirmDelete(true)}>
                    Delete
                  </button>
                )
              ) : null}
              {actions.live ? <CategorySelect comment={entry} fragmentId={entry.fragmentId} categories={categories} live={actions.live} onEdit={onEditCategories} /> : null}
            </>
          )}
        </div>
      ) : null}
      {replying ? (
        <form
          className="reply-form"
          onSubmit={(e) => {
            e.preventDefault();
            sendReply();
          }}
        >
          <label className="visually-hidden" htmlFor={`reply-${entry.id}`}>
            Reply
          </label>
          <textarea
            id={`reply-${entry.id}`}
            rows={2}
            value={reply}
            autoFocus
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendReply();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                setReplying(false);
              }
            }}
          />
          <div className="actions">
            <button type="button" className="button secondary small" onClick={() => setReplying(false)}>
              Cancel
            </button>
            <button type="submit" className="button primary small" disabled={!reply.trim()}>
              Reply
            </button>
          </div>
        </form>
      ) : null}
    </article>
  );
}

/** A modal that asks for some text (a comment, a checkpoint's name): Escape or Cancel says no. */
export function TextDialog({
  title,
  label,
  initial = '',
  confirm,
  multiline,
  message,
  extra,
  onDone,
  onCancel,
}: {
  title: string;
  label: string;
  initial?: string;
  confirm: string;
  multiline?: boolean;
  message?: React.ReactNode;
  /** More fields under the text (Add Comment's category). */
  extra?: React.ReactNode;
  onDone: (text: string) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [text, setText] = useState(initial);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const done = () => {
    if (text.trim()) onDone(text.trim());
  };
  return (
    <dialog
      ref={ref}
      className="confirm text-dialog"
      aria-labelledby="text-dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          done();
        }}
      >
        <h2 id="text-dialog-title">{title}</h2>
        {message ? <div className="confirm-message">{message}</div> : null}
        <label className="field-label stacked-label" htmlFor="text-dialog-field">
          {label}
        </label>
        {multiline ? (
          <textarea
            id="text-dialog-field"
            rows={4}
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                done();
              }
            }}
          />
        ) : (
          <input id="text-dialog-field" type="text" value={text} autoFocus onChange={(e) => setText(e.target.value)} onFocus={(e) => e.target.select()} />
        )}
        {extra}
        <div className="actions">
          <button type="button" className="button secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={!text.trim()}>
            {confirm}
          </button>
        </div>
      </form>
    </dialog>
  );
}
